/**
 * PasswordStrength — the lines under every new-password field.
 *
 * Four bars for how guessable the password is (`usePasswordStrength`, 0–4),
 * the one rule — "At least 10 characters", ticked when met, with the strength
 * word beside it — and a line of advice. The bars advise; the rule
 * (`meetsRule`, from `meetsPasswordRule` in @tonnie/schemas) is what the form
 * enforces. The rule line is announced politely as it changes.
 *
 * Texts are passed in translated, like every other text this package renders.
 */

import { Check, Circle } from 'lucide-react';

import { cn } from '../lib/utils';
import {
  passwordStrengthLevel,
  usePasswordStrength,
  type PasswordStrengthLevel,
} from '../lib/use-password-strength';

export interface PasswordStrengthLabels {
  /** "At least 10 characters" */
  requirement: string;
  /** "A few words strung together are strong and easy to remember." */
  advice: string;
  /** The word for each score band. */
  levels: { weak: string; fair: string; good: string; strong: string };
}

export interface PasswordStrengthProps {
  password: string;
  meetsRule: boolean;
  labels: PasswordStrengthLabels;
  /** The person's name and email — a password built from them scores low. */
  userInputs?: readonly string[];
  id?: string;
  className?: string;
}

const BAR_COLOR: Record<PasswordStrengthLevel, string> = {
  weak: 'bg-destructive',
  fair: 'bg-warning',
  good: 'bg-success',
  strong: 'bg-success',
};

export function PasswordStrength({
  password,
  meetsRule,
  labels,
  userInputs,
  id,
  className,
}: PasswordStrengthProps) {
  const score = usePasswordStrength(password, userInputs);
  const level = score === null ? null : passwordStrengthLevel(score);
  const filled = score === null ? 0 : Math.max(score, 1);

  return (
    <div id={id} className={cn('space-y-2', className)} data-testid="password-strength">
      <div className="grid grid-cols-4 gap-1" aria-hidden="true">
        {[1, 2, 3, 4].map((bar) => (
          <span
            key={bar}
            className={cn(
              'h-1 rounded-full transition-colors',
              level && bar <= filled ? BAR_COLOR[level] : 'bg-border'
            )}
          />
        ))}
      </div>
      <p
        aria-live="polite"
        data-met={meetsRule}
        data-level={level ?? undefined}
        className={cn(
          'flex items-center gap-1.5 text-sm font-medium',
          meetsRule ? 'text-success-accessible' : 'text-muted-foreground'
        )}
      >
        {meetsRule ? (
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Circle className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        <span>{labels.requirement}</span>
        {meetsRule && level && (
          <>
            <span aria-hidden="true">·</span>
            <span>{labels.levels[level]}</span>
          </>
        )}
      </p>
      <p className="text-sm leading-relaxed text-muted-foreground">{labels.advice}</p>
    </div>
  );
}
